import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { callEdge } from '@/lib/edgeClient';

interface PoseAnalysisResult {
  ok: boolean;
  analysis: string;
  frames_analyzed: number;
  analysis_id: string | null;
}

export function usePoseAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<PoseAnalysisResult | null>(null);
  const { toast } = useToast();

  const analyzeFrames = async (analysisId: string) => {
    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const response = await callEdge('pose-analyze', {
        method: 'POST',
        body: { analysis_id: analysisId },
        authRequired: true
      });

      if (response.status !== 200) {
        throw new Error(response.body?.error || 'Failed to analyze pose data');
      }

      const result = response.body as PoseAnalysisResult;
      setAnalysisResult(result);

      if (result.ok) {
        toast({
          title: "Analysis Complete",
          description: `Analyzed ${result.frames_analyzed} frames successfully`,
        });
      } else {
        throw new Error('Analysis failed');
      }

      return result;
    } catch (error) {
      console.error('Pose analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error instanceof Error ? error.message : "Failed to analyze pose data",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearResult = () => {
    setAnalysisResult(null);
  };

  return {
    analyzeFrames,
    clearResult,
    isAnalyzing,
    analysisResult
  };
}